import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AdminUser, CreateUserData, UpdateUserData } from '@/hooks/useAdminUsers';

interface UserFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: AdminUser | null;
  onSubmit: (data: CreateUserData | UpdateUserData) => Promise<boolean>;
}

export const UserFormDialog = ({ open, onOpenChange, user, onSubmit }: UserFormDialogProps) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    prenom: '',
    nom: '',
    role: 'user',
    formule: 'mensuel' as 'gratuit' | 'mensuel' | 'annuel' | 'partenaire',
    date_debut: '',
    date_fin: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!user;

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        password: '',
        prenom: user.prenom || '',
        nom: user.nom || '',
        role: user.role || 'user',
        formule: (() => {
          const plan = user.abonnements?.[0]?.plan;
          if (plan === 'gratuit') return 'gratuit';
          if (plan === 'partenaire') return 'partenaire';
          if (plan === 'annuel') return 'annuel';
          if (plan === 'mensuel') return 'mensuel';
          // Fallback basé sur la durée
          const d0 = user.abonnements?.[0]?.date_debut;
          const d1 = user.abonnements?.[0]?.date_fin;
          if (d0 && d1) {
            const start = new Date(d0);
            const end = new Date(d1);
            const diffDays = Math.abs(+end - +start) / (1000 * 60 * 60 * 24);
            if (diffDays > 330) return 'annuel';
            if (diffDays > 60) return 'partenaire';
          }
          return 'mensuel';
        })(),
        date_debut: user.abonnements?.[0]?.date_debut ? user.abonnements[0].date_debut.slice(0, 10) : '',
        date_fin: user.abonnements?.[0]?.date_fin ? user.abonnements[0].date_fin.slice(0, 10) : '',
      });
    } else {
      setFormData({
        email: '',
        password: '',
        prenom: '',
        nom: '',
        role: 'user',
        formule: 'mensuel',
        date_debut: '',
        date_fin: '',
      });
    }
  }, [user, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submitData = isEditing 
        ? {
            email: formData.email,
            prenom: formData.prenom,
            nom: formData.nom,
            role: formData.role,
            plan: formData.formule,
            formule: formData.formule,
            date_debut: formData.date_debut ? formData.date_debut : null,
            date_fin: formData.date_fin ? formData.date_fin : null,
          }
        : {
            email: formData.email,
            password: formData.password,
            prenom: formData.prenom,
            nom: formData.nom,
            role: formData.role,
            plan: formData.formule,
            formule: formData.formule,
            date_debut: formData.date_debut ? formData.date_debut : null,
            date_fin: formData.date_fin ? formData.date_fin : null,
          };

      console.log('UserFormDialog: Submitting data', submitData);
      const success = await onSubmit(submitData);
      console.log('UserFormDialog: Submit result', success);
      if (success) {
        onOpenChange(false);
      } else {
        console.error('UserFormDialog: Submit failed');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value } as typeof prev;
      
      // Si on change le forfait en mode édition, mettre la date de début à aujourd'hui
      if (field === 'formule' && isEditing) {
        const today = new Date().toISOString().slice(0, 10);
        next.date_debut = today;
      }
      
      // Recalculer la date de fin si nécessaire
      if ((field === 'date_debut' || field === 'formule') && next.date_debut) {
        const d = new Date(next.date_debut);
        if (!isNaN(d.getTime())) {
          if (next.formule === 'annuel') {
            d.setFullYear(d.getFullYear() + 1);
          } else if (next.formule === 'partenaire') {
            d.setMonth(d.getMonth() + 3); // Partenaire = 3 mois premium
          } else if (next.formule === 'mensuel') {
            d.setMonth(d.getMonth() + 1);
          }
          // Pour 'gratuit', pas de date de fin automatique
          if (next.formule !== 'gratuit') {
            next.date_fin = d.toISOString().slice(0, 10);
          } else {
            next.date_fin = '';
          }
        }
      }
      
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Modifier l\'utilisateur' : 'Créer un utilisateur'}
          </DialogTitle>
          <DialogDescription>
            Gérez les informations et le forfait de l'utilisateur.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="prenom">Prénom</Label>
              <Input
                id="prenom"
                value={formData.prenom}
                onChange={(e) => handleInputChange('prenom', e.target.value)}
                placeholder="Prénom"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nom">Nom</Label>
              <Input
                id="nom"
                value={formData.nom}
                onChange={(e) => handleInputChange('nom', e.target.value)}
                placeholder="Nom"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="email@exemple.com"
              required
            />
          </div>

          {!isEditing && (
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Mot de passe"
                required
                minLength={6}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="role">Rôle</Label>
              <Select value={formData.role} onValueChange={(value) => handleInputChange('role', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un rôle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Utilisateur</SelectItem>
                  <SelectItem value="admin">Administrateur</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="formule">Formule</Label>
              <Select value={formData.formule} onValueChange={(value) => handleInputChange('formule', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une formule" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gratuit">Gratuit</SelectItem>
                  <SelectItem value="mensuel">Mensuel</SelectItem>
                  <SelectItem value="annuel">Annuel</SelectItem>
                  <SelectItem value="partenaire">Partenaire (3 mois)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date_debut">Date de début</Label>
              <Input
                id="date_debut"
                type="date"
                value={formData.date_debut}
                onChange={(e) => handleInputChange('date_debut', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="date_fin">Date de fin</Label>
              <Input
                id="date_fin"
                type="date"
                value={formData.date_fin}
                onChange={(e) => handleInputChange('date_fin', e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'En cours...' : (isEditing ? 'Modifier' : 'Créer')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};